import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

interface PageData {
  publication: {
    id: number;
    name: string;
    account: { id: number; name: string };
    contentStrategyCompleted: boolean;
  };
  talentRequest: {
    id: number;
    title: string;
  };
  customTalentRequest: {
    id: number;
  };
  declinedUserIds: number[];
  dismissedUserIds: number[];
  declinedReasons: Record<number, string>;
  existingContributorUserIds: number[];
}

interface UserOption {
  id: number;
  name: string;
  email: string;
  talentStatus?: string;
}

interface Clip {
  id: number;
  title: string;
}

interface ClipsResponse {
  error?: string;
  warning?: string;
  info?: string;
  user?: {
    id: number;
    name: string;
    email: string;
    talentStatus?: string;
  };
  clips: Clip[];
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  flex-wrap: wrap;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0 0 24px 0;
`;

const WarningBanner = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label<{ $required?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;

  ${({ $required }) =>
    $required &&
    `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 3px rgba(0, 166, 194, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  max-width: 400px;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 3px rgba(0, 166, 194, 0.1);
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-width: 400px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
`;

const UserOptionItem = styled.div<{ $selected?: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#f3f4f6' : 'white')};
  
  &:hover {
    background: #f3f4f6;
  }
  
  .name { font-weight: 500; }
  .email { font-size: 12px; color: #6b7280; }
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const ClipsSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 4px;
`;

const ClipMessage = styled.div<{ $type?: 'error' | 'warning' | 'info' }>`
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  background: ${({ $type }) => 
    $type === 'error' ? '#fef2f2' : 
    $type === 'warning' ? '#fef3c7' : 
    '#f0f9ff'};
  color: ${({ $type }) => 
    $type === 'error' ? '#dc2626' : 
    $type === 'warning' ? '#92400e' : 
    '#0369a1'};
  border: 1px solid ${({ $type }) => 
    $type === 'error' ? '#fecaca' : 
    $type === 'warning' ? '#f59e0b' : 
    '#bae6fd'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $primary }) =>
    $primary
      ? `
    background: #00a6c2;
    color: white;
    border: none;
    &:hover { background: #0891b2; }
    &:disabled { background: #9ca3af; cursor: not-allowed; }
  `
      : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    &:hover { background: #f9fafb; }
  `}
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const WarningModal = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const ModalText = styled.p`
  margin: 0 0 24px 0;
  color: #374151;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

function AdminAddRecommendedContributorPage() {
  const { id, talentRequestId, ctrId } = useParams<{ 
    id: string; 
    talentRequestId: string; 
    ctrId: string;
  }>();
  const navigate = useNavigate();
  
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // User search state
  const [searchQuery, setSearchQuery] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Clips state
  const [clipsData, setClipsData] = useState<ClipsResponse | null>(null);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [selectedClips, setSelectedClips] = useState<(number | null)[]>([null, null, null]);
  
  // Warning modal state
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const fetchPageData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `/admin2/publications/${id}/talent_requests/${talentRequestId}/custom_talent_requests/${ctrId}/recommended_contributors/new.json`
      );
      setPageData(res.data);
    } catch (err) {
      console.error('Failed to load page data', err);
      setError('Failed to load page data');
    } finally {
      setLoading(false);
    }
  }, [id, talentRequestId, ctrId]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  // Debounced user search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setUserOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get('/admin2/users/autocomplete.json', {
          params: { query: searchQuery }
        });
        setUserOptions(res.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('User search failed', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load clips when user is selected
  useEffect(() => {
    if (!selectedUser) {
      setClipsData(null);
      return;
    }

    const loadClips = async () => {
      setClipsLoading(true);
      try {
        const res = await api.get(
          `/admin2/publications/${id}/talent_requests/${talentRequestId}/custom_talent_requests/${ctrId}/recommended_contributors/users/${selectedUser.id}.json`
        );
        setClipsData(res.data);
        setSelectedClips([null, null, null]);
      } catch (err) {
        console.error('Failed to load clips', err);
        setClipsData({ error: 'Failed to load clips', clips: [] });
      } finally {
        setClipsLoading(false);
      }
    };

    loadClips();
  }, [selectedUser, id, talentRequestId, ctrId]);

  const handleUserSelect = (user: UserOption) => {
    setSelectedUser(user);
    setSearchQuery(user.name);
    setShowDropdown(false);
  };

  const handleClipChange = (index: number, clipId: string) => {
    const newClips = [...selectedClips];
    newClips[index] = clipId ? parseInt(clipId, 10) : null;
    setSelectedClips(newClips);
  };

  const checkWarnings = (): string | null => {
    if (!selectedUser || !pageData) return null;
    
    const userId = selectedUser.id;
    
    if (pageData.declinedUserIds.includes(userId)) {
      const reason = pageData.declinedReasons[userId];
      return `previously declined to work with this publication because they were ${reason}.`;
    }
    
    if (pageData.dismissedUserIds.includes(userId)) {
      return 'was previously dismissed by this publication.';
    }
    
    if (selectedUser.talentStatus === 'banned') {
      return 'was previously banned.';
    }
    
    return null;
  };

  const handleSubmit = async (force = false) => {
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }

    // Check for warnings
    if (!force) {
      const warning = checkWarnings();
      if (warning) {
        setWarningMessage(`The contributor you are recommending ${warning}`);
        setShowWarningModal(true);
        return;
      }
    }

    // Validate clips if required
    const hasClips = clipsData?.clips && clipsData.clips.length > 0;
    const isAlreadyRecommended = clipsData?.info;
    
    if (hasClips && !isAlreadyRecommended) {
      const filledClips = selectedClips.filter(c => c !== null);
      if (filledClips.length < 3) {
        alert('Please select 3 clips for this contributor');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        recommended_contributor: {
          user_id: selectedUser.id,
          talent_request_id: talentRequestId,
          recommended_contributor_clips_attributes: selectedClips
            .filter(c => c !== null)
            .map((clipId) => ({ clip_id: clipId }))
        }
      };

      await api.post(
        `/admin2/publications/${id}/talent_requests/${talentRequestId}/custom_talent_requests/${ctrId}/recommended_contributor.json`,
        payload
      );

      navigate(`/admin2/publications/${id}/talent_requests/${talentRequestId}`);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to add recommended contributor';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
      setShowWarningModal(false);
    }
  };

  const pubDisplayName = pageData?.publication 
    ? `${pageData.publication.account?.name} - ${pageData.publication.name}` 
    : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Add Recommended Contributor" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Add Recommended Contributor" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Talent requests', href: `/admin2/publications/${id}/talent_requests` },
        { label: pageData?.talentRequest?.title || 'Talent Request', href: `/admin2/publications/${id}/talent_requests/${talentRequestId}` },
        { label: 'Custom talent requests' },
        { label: 'Add recommended contributor' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{pubDisplayName}</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/talent_requests`}>Talent requests</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/talent_requests/${talentRequestId}`}>{pageData?.talentRequest?.title}</Link>
        <span>›</span>
        <span>Custom talent requests</span>
        <span>›</span>
        <span>Add recommended contributor</span>
      </Breadcrumb>

      <PageTitle>Add a recommended contributor</PageTitle>

      {!pageData?.publication?.contentStrategyCompleted && (
        <WarningBanner>
          WARNING! THIS PUBLICATION HAS NOT INPUT THEIR CONTENT STRATEGY IN CONTENTLY.
        </WarningBanner>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Section>
        <FormGroup>
          <Label $required>User</Label>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Start typing to find a user"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value) {
                  setSelectedUser(null);
                }
              }}
              onFocus={() => userOptions.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />
            {showDropdown && userOptions.length > 0 && (
              <UserDropdown>
                {userOptions.map((user) => (
                  <UserOptionItem
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    $selected={selectedUser?.id === user.id}
                  >
                    <div className="name">{user.name}</div>
                    <div className="email">{user.email}</div>
                  </UserOptionItem>
                ))}
              </UserDropdown>
            )}
          </SearchWrapper>
          {searchLoading && <div style={{ marginTop: 8, color: '#6b7280' }}>Searching...</div>}
          
          {selectedUser?.talentStatus === 'banned' && (
            <ErrorBanner style={{ marginTop: 12 }}>
              This contributor was previously banned.
            </ErrorBanner>
          )}
        </FormGroup>

        {/* Clips Section */}
        {selectedUser && (
          <ClipsSection>
            {clipsLoading ? (
              <div>Loading clips...</div>
            ) : clipsData?.error ? (
              <ClipMessage $type="error">{clipsData.error}</ClipMessage>
            ) : clipsData?.warning ? (
              <ClipMessage $type="warning">{clipsData.warning}</ClipMessage>
            ) : clipsData?.info ? (
              <ClipMessage $type="info">{clipsData.info}</ClipMessage>
            ) : clipsData?.clips && clipsData.clips.length > 0 ? (
              <>
                {[0, 1, 2].map((idx) => (
                  <FormGroup key={idx}>
                    <Label $required>Clip {idx + 1}</Label>
                    <Select
                      value={selectedClips[idx] || ''}
                      onChange={(e) => handleClipChange(idx, e.target.value)}
                      required
                    >
                      <option value="">Select a clip...</option>
                      {clipsData.clips.map((clip) => (
                        <option key={clip.id} value={clip.id}>
                          {clip.title}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                ))}
              </>
            ) : (
              <ClipMessage $type="info">
                The user {selectedUser.name} has no clips to add.
              </ClipMessage>
            )}
          </ClipsSection>
        )}

        <ButtonGroup>
          <Button
            onClick={() => navigate(`/admin2/publications/${id}/talent_requests/${talentRequestId}`)}
          >
            Cancel
          </Button>
          <Button
            $primary
            onClick={() => handleSubmit()}
            disabled={!selectedUser || submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </ButtonGroup>
      </Section>

      {/* Warning Modal */}
      <WarningModal $visible={showWarningModal}>
        <ModalContent>
          <ModalTitle>Warning</ModalTitle>
          <ModalText>{warningMessage}</ModalText>
          <ModalButtons>
            <Button onClick={() => setShowWarningModal(false)}>Cancel</Button>
            <Button $primary onClick={() => handleSubmit(true)}>
              Recommend anyway
            </Button>
          </ModalButtons>
        </ModalContent>
      </WarningModal>
    </AdminLayout>
  );
}

export default AdminAddRecommendedContributorPage;
